'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Sparkles, Camera, Users, Music, Video, ImageIcon, Briefcase, AtSign, Zap, FileText, Hash, Lightbulb, Layers, MessageSquare, Lock, Info, ChevronDown, Star } from 'lucide-react';
import MarketingNav from '@/components/MarketingNav';
import MarketingFooter from '@/components/MarketingFooter';
import { useLanguage } from '@/context/LanguageContext';

// ─── Demo usage tracking (localStorage, no IP, no AI calls) ──────────────────

const DEMO_STORAGE_KEY = 'aimaker_demo_generations_used';
const DEMO_MAX_GENERATIONS = 2;
const DEMO_LAST_STATE_KEY = 'aimaker_demo_last_state';
const DEMO_USAGE_TTL_MS = 24 * 60 * 60 * 1000;

interface DemoUsageRecord {
  count: number;
  ts: number;
}

function clearDemoUsage(): void {
  try {
    localStorage.removeItem(DEMO_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Entries older than the TTL — and legacy entries stored without a timestamp —
 * reset to zero. Unreadable storage counts as unused so the demo never locks.
 */
function getDemoUsageCount(): number {
  if (typeof window === 'undefined') return 0;
  try {
    const stored = localStorage.getItem(DEMO_STORAGE_KEY);
    if (!stored) return 0;
    const parsed = JSON.parse(stored) as Partial<DemoUsageRecord> | null;
    if (typeof parsed?.count !== 'number' || typeof parsed?.ts !== 'number') {
      clearDemoUsage();
      return 0;
    }
    if (Date.now() - parsed.ts > DEMO_USAGE_TTL_MS) {
      clearDemoUsage();
      return 0;
    }
    return Math.min(Math.max(parsed.count, 0), DEMO_MAX_GENERATIONS);
  } catch {
    return 0;
  }
}

/**
 * Best effort only. Safari Private Browsing and blocked-cookie modes throw here,
 * and the in-memory count still enforces the limit for the current session.
 */
function persistDemoUsage(count: number): void {
  if (typeof window === 'undefined') return;
  try {
    const record: DemoUsageRecord = { count, ts: Date.now() };
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // ignore
  }
}

interface DemoLastState {
  platform: string;
  contentType: string;
  activeTab: string;
  hasGenerated: boolean;
}

function saveDemoLastState(state: DemoLastState): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(DEMO_LAST_STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

function loadDemoLastState(): DemoLastState | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(DEMO_LAST_STATE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as DemoLastState;
  } catch {
    return null;
  }
}

// ─── Preview truncation helpers ───────────────────────────────────────────────

function getPreviewContent(key: string, fullContent: string): { preview: string; hasMore: boolean } {
  switch (key) {
    case 'hook':
      // Hook is short — show fully
      return { preview: fullContent, hasMore: false };
    case 'caption': {
      // Show first ~120 chars then lock
      const lines = fullContent.split('\n');
      const firstTwo = lines.slice(0, 2).join('\n');
      const preview = firstTwo.length > 120 ? firstTwo.slice(0, 120) + '…' : firstTwo + (lines.length > 2 ? '…' : '');
      return { preview, hasMore: fullContent.length > preview.replace('…', '').length };
    }
    case 'cta':
      // CTA is short — show fully
      return { preview: fullContent, hasMore: false };
    case 'hashtags': {
      // Show first 4 hashtags only
      const tags = fullContent.split(' ');
      const preview = tags.slice(0, 4).join(' ');
      return { preview, hasMore: tags.length > 4 };
    }
    case 'imagePrompt': {
      // Show first ~80 chars
      const preview = fullContent.length > 80 ? fullContent.slice(0, 80) + '…' : fullContent;
      return { preview, hasMore: fullContent.length > 80 };
    }
    case 'videoScript': {
      // Show HOOK section only
      const hookEnd = fullContent.indexOf('\n', fullContent.indexOf('MAIN'));
      const cutAt = hookEnd > 0 ? hookEnd : Math.min(fullContent.length, 120);
      const preview = fullContent.slice(0, cutAt) + (cutAt < fullContent.length ? '…' : '');
      return { preview, hasMore: cutAt < fullContent.length };
    }
    case 'postingTips': {
      // Show first 2 tips only
      const tips = fullContent.split('\n').filter(Boolean);
      const preview = tips.slice(0, 2).join('\n');
      return { preview, hasMore: tips.length > 2 };
    }
    case 'variations': {
      // Show first variation only
      const vars = fullContent.split('\n').filter(Boolean);
      const preview = vars.slice(0, 1).join('\n');
      return { preview, hasMore: vars.length > 1 };
    }
    default:
      return { preview: fullContent.slice(0, 100) + (fullContent.length > 100 ? '…' : ''), hasMore: fullContent.length > 100 };
  }
}

// ─── 12 polished business-specific demo samples ───────────────────────────────

const DEMO_OUTPUTS: Record<string, Record<string, Record<string, string>>> = {
  Instagram: {
    Restaurant: {
      hook: '🍽️ The dish everyone is talking about — and it\'s only available this weekend.',
      caption: 'Some flavors are worth the wait. 🌿\n\nOur chef just dropped a limited weekend special that\'s already selling out by Saturday afternoon.\n\nFresh ingredients. Bold flavors. A dining experience you\'ll want to come back for.\n\n📍 Reserve your table now — link in bio.',
      cta: 'Reserve your table tonight — tap the link in bio before it\'s fully booked.',
      hashtags: '#Restaurant #FoodLovers #WeekendSpecial #FarmToTable #LocalEats #FoodPhotography #DineOut #ChefSpecial #FoodieLife #SupportLocal',
      imagePrompt: 'Beautifully plated gourmet dish on a dark slate plate, soft candlelight ambiance, shallow depth of field, warm restaurant interior, professional food photography, 8k',
      videoScript: 'HOOK (0–5s): Close-up of a sizzling dish arriving at the table — steam rising, garnish placed.\nMAIN (5–25s): Chef plating the dish in the kitchen, cut to happy guests enjoying the meal, quick shots of fresh ingredients.\nOUTRO CTA (25–30s): Restaurant logo fade-in. "Reserve your table — link in bio."',
      postingTips: '1. Lead with a strong first sentence that creates appetite appeal.\n2. Post during lunch (11am–1pm) or dinner prep hours (4–6pm) when food cravings peak.\n3. Include one clear call-to-action — reservation link, phone number, or address.\n4. Respond to comments and DMs promptly to convert interest into bookings.\n5. Stay visually consistent — use the same filter or lighting style across all food posts.',
      variations: '1. "Behind the scenes" — show the chef prepping the dish in the kitchen.\n2. Customer reaction video — capture a genuine first-bite moment.\n3. Ingredient spotlight — feature one hero ingredient and its origin story.',
    },
    'Coffee Shop': {
      hook: '☕ Your morning just got an upgrade. Meet our new seasonal latte.',
      caption: 'Fall is here and so is our Maple Brown Sugar Latte. 🍂\n\nHandcrafted with locally sourced maple syrup, oat milk, and our signature espresso blend.\n\nWarm. Cozy. Exactly what your morning deserves.\n\n📍 Available now at all locations — while supplies last.',
      cta: 'Come in this week and try it — show this post for a free size upgrade.',
      hashtags: '#CoffeeShop #LatteArt #SeasonalDrinks #CoffeeLover #MorningCoffee #LocalCafe #SpecialtyCoffee #CoffeeTime #FallVibes #CaffeineAndDreams',
      imagePrompt: 'Artisan latte with leaf art in a ceramic mug, warm autumn tones, cozy cafe window with soft natural light, rustic wooden table, professional beverage photography',
      videoScript: 'HOOK (0–5s): Barista pouring steamed milk into espresso — latte art forming in slow motion.\nMAIN (5–25s): Seasonal ingredients displayed, drink being crafted step by step, cozy cafe atmosphere shots.\nOUTRO CTA (25–30s): "New seasonal latte — available now. Come visit us."',
      postingTips: '1. Post between 6–9am to catch the morning coffee crowd scrolling before their first cup.\n2. Use relevant, specific hashtags rather than overly broad ones.\n3. Invite followers to tag a friend they\'d bring — it extends your organic reach.\n4. Test different hooks: "Your morning just got better" vs. "We need to talk about this latte."\n5. Post consistently — even 3x per week builds a loyal local following.',
      variations: '1. "How it\'s made" Reel — 15-second barista process video.\n2. Customer feature — repost a tagged photo with permission.\n3. Pairing suggestion — show the latte with a pastry or breakfast item.',
    },
    'Tattoo Studio': {
      hook: '🖤 This piece took 6 hours. The story behind it took a lifetime.',
      caption: 'Every tattoo we create starts with a conversation. 🎨\n\nThis custom sleeve was inspired by our client\'s grandmother\'s garden — every flower, every leaf chosen with intention.\n\nThis is more than ink. It\'s a story you carry with you forever.\n\n📩 DM us to book your consultation. Custom slots are limited.',
      cta: 'DM us to start your custom design consultation — limited bookings available this month.',
      hashtags: '#TattooArtist #CustomTattoo #TattooDesign #InkLife #TattooStudio #TattooInspiration #FineLineTattoo #TattooArt #BodyArt #TattooLovers',
      imagePrompt: 'Detailed fine-line botanical tattoo sleeve on forearm, soft studio lighting, clean white background, professional tattoo photography, high contrast, sharp detail',
      videoScript: 'HOOK (0–5s): Time-lapse of a tattoo being drawn — needle moving across skin in close-up.\nMAIN (5–25s): Artist sketching the design, client consultation, finished piece reveal with client reaction.\nOUTRO CTA (25–30s): Studio name. "Book your consultation — DM us."',
      postingTips: '1. Lead with the story behind the piece — emotional context drives saves and shares.\n2. Always get client permission before posting their tattoo.\n3. Include one clear booking CTA in every post — DM, link in bio, or phone number.\n4. Use niche hashtags like #FineLineTattoo or #BotanicalTattoo alongside broader ones.\n5. Respond to every comment — your community is your best marketing.',
      variations: '1. "Before and after" — sketch vs. finished tattoo side by side.\n2. Artist spotlight — introduce your team and their specialty styles.\n3. Client story — with permission, share the meaning behind a meaningful piece.',
    },
  },
  TikTok: {
    "Children's Book": {
      hook: '📚 I wrote a children\'s book in my living room. Here\'s what happened next.',
      caption: 'Two years ago I had an idea for a story. Today it\'s on shelves in 3 countries. 🌍\n\nHere\'s the honest journey — the rejections, the rewrites, and the moment it finally clicked.\n\n#childrensbook #selfpublishing #authorlife #indieauthor #kidlit',
      cta: 'Follow for the full story — part 2 drops tomorrow!',
      hashtags: '#childrensbook #selfpublishing #authorlife #indieauthor #kidlit #bookstagram #writersoftiktok #storytime #publishingjourney #booktok',
      imagePrompt: 'Colorful children\'s book open on a cozy reading nook, soft natural light, illustrated pages visible, warm and inviting atmosphere, lifestyle photography',
      videoScript: 'HOOK (0–5s): Hold up the book to camera. "This took me 2 years and 47 rejections."\nMAIN (5–25s): Flip through the pages, share key moments of the journey — first rejection letter, first yes, first copy in hand.\nOUTRO CTA (25–30s): "Follow for part 2 — how I got my first 1,000 readers."',
      postingTips: '1. Lead with a specific number or surprising fact — it stops the scroll.\n2. Break your story into a series — multi-part content drives follows.\n3. Respond to every comment, especially questions — it signals to the algorithm.\n4. Post consistently — 3–5x per week is ideal for TikTok growth.\n5. Test different hooks for the same story — small changes can double your views.',
      variations: '1. "Read-aloud" clip — film yourself reading a page to a child.\n2. "Behind the illustrations" — show the art process with the illustrator.\n3. "Storytime" format — tell the book\'s origin story in under 60 seconds.',
    },
    'Etsy Store': {
      hook: '🛍️ I made $8,000 on Etsy last month selling something I make in 20 minutes.',
      caption: 'No, it\'s not a scam. Yes, I\'ll show you exactly how. 👇\n\nI started my Etsy shop 18 months ago with $200 and a Cricut machine.\n\nHere\'s what actually works — and what everyone gets wrong.\n\n#etsyseller #etsyshop #sidehustle #makemoney #smallbusiness',
      cta: 'Follow for my free Etsy starter guide — dropping this Friday!',
      hashtags: '#etsyseller #etsyshop #sidehustle #makemoney #smallbusiness #handmade #cricut #cricutcreations #passiveincome #entrepreneurlife',
      imagePrompt: 'Flat lay of handmade craft products on a clean white background, natural light, product tags visible, professional product photography, warm tones',
      videoScript: 'HOOK (0–5s): Show a phone screen with Etsy sales dashboard — "$8,247 this month."\nMAIN (5–25s): Walk through the product, the process, the pricing strategy, and one key mistake to avoid.\nOUTRO CTA (25–30s): "Follow — free guide drops Friday."',
      postingTips: '1. Lead with a specific, believable number — vague claims get scrolled past.\n2. Deliver real value in every video — your audience will grow faster.\n3. Include one clear CTA per video — follow, link in bio, or comment a keyword.\n4. Stay visually consistent with your brand aesthetic across all content.\n5. Engage with comments within the first hour — it boosts early distribution.',
      variations: '1. "Day in my life as an Etsy seller" — show the full process.\n2. "Packaging orders" ASMR-style video — highly shareable.\n3. "What I wish I knew" — common beginner mistakes and how to avoid them.',
    },
    'Fitness Coach': {
      hook: '💪 I lost 40 lbs without giving up carbs. Here\'s the only thing that actually worked.',
      caption: 'Forget everything you\'ve been told about weight loss. 🙅‍♀️\n\nAfter 3 years of trying every diet, I finally found what works — and it\'s not what the fitness industry wants you to buy.\n\nThis one habit changed everything.\n\n#fitnesscoach #weightloss #healthylifestyle #fitnesstips #transformation',
      cta: 'Follow for my free 7-day kickstart plan — no equipment needed!',
      hashtags: '#fitnesscoach #weightloss #healthylifestyle #fitnesstips #transformation #workoutroutine #nutritioncoach #fitfam #healthjourney #gymlife',
      imagePrompt: 'Fitness coach in athletic wear demonstrating an exercise outdoors, natural light, energetic and motivational composition, clean background, professional fitness photography',
      videoScript: 'HOOK (0–5s): "I lost 40 lbs without giving up carbs — here\'s the one thing that actually worked."\nMAIN (5–25s): Share the habit, explain why it works, show a quick before/after or demonstration.\nOUTRO CTA (25–30s): "Follow for my free 7-day plan — link in bio."',
      postingTips: '1. Lead with a transformation or counterintuitive claim — it stops the scroll.\n2. Be specific — "40 lbs" outperforms "lost weight" every time.\n3. Deliver the promised value in the video — don\'t bait and switch.\n4. Post consistently — fitness audiences respond to routine creators.\n5. Test different hooks for the same content — small tweaks can 10x your reach.',
      variations: '1. "Day in my life" — show your actual meals and workouts.\n2. "Myth busting" — debunk a common fitness misconception.\n3. "Client transformation" — with permission, share a client\'s journey.',
    },
  },
  LinkedIn: {
    Realtor: {
      hook: 'I\'ve sold 200+ homes. Here are the 5 things buyers always regret not asking.',
      caption: 'After 12 years in real estate, I\'ve seen the same mistakes over and over.\n\nBuyers get excited, skip the hard questions, and end up with regrets.\n\nHere\'s what to ask before you make an offer:\n\n1️⃣ Why is the seller moving?\n2️⃣ How long has it been on the market — and why?\n3️⃣ What are the actual monthly costs (HOA, taxes, insurance)?\n4️⃣ What\'s the neighborhood like at 10pm on a Friday?\n5️⃣ What would you fix if you were staying?\n\nThe answers will tell you everything.\n\nSave this for your next home search. 🏡',
      cta: 'Follow me for weekly real estate insights — and DM me if you\'re ready to buy or sell.',
      hashtags: '#RealEstate #HomeBuying #Realtor #RealEstateTips #HousingMarket #FirstTimeHomeBuyer #PropertyInvestment #RealEstateAgent #HomeSearch #RealEstateAdvice',
      imagePrompt: 'Professional realtor standing in front of a beautiful home, confident posture, natural outdoor lighting, well-dressed, welcoming expression, real estate photography',
      videoScript: 'HOOK (0–5s): "I\'ve sold 200 homes. Here are 5 questions buyers always forget to ask."\nMAIN (5–25s): Walk through each question with a brief explanation of why it matters.\nOUTRO CTA (25–30s): "Follow for weekly real estate tips. DM me if you\'re ready to move."',
      postingTips: '1. Lead with a specific credential — "200+ homes" builds instant authority.\n2. Use numbered lists — they\'re easy to read and highly shareable on LinkedIn.\n3. Ask a question at the end to drive comments and extend reach.\n4. Post Tuesday–Thursday between 8–10am for peak LinkedIn engagement.\n5. Avoid external links in the post body — put them in the first comment.',
      variations: '1. "Red flags to watch for" — warning signs in a home showing.\n2. "Seller\'s perspective" — what sellers wish buyers knew.\n3. "Market update" — local stats with your professional take.',
    },
    Dentist: {
      hook: 'I\'m a dentist. Here\'s what I actually do differently for my own teeth.',
      caption: 'Patients always ask me: "What do YOU use?"\n\nHere\'s the honest answer — no sponsored products, no affiliate links.\n\n🦷 What I actually do:\n\n1️⃣ Electric toothbrush — the oscillating kind, not sonic\n2️⃣ Floss before brushing, not after\n3️⃣ Fluoride toothpaste — always\n4️⃣ Mouthwash at a different time than brushing\n5️⃣ Tongue scraper every morning\n\nThe biggest thing most people skip? Flossing. And it shows.\n\nWhat question do you have about your dental health? Drop it below. 👇',
      cta: 'Follow for weekly dental health tips — no jargon, just practical advice.',
      hashtags: '#Dentist #DentalHealth #OralCare #TeethWhitening #DentalTips #SmileMore #HealthyTeeth #DentalHygiene #OralHealth #DentistryLife',
      imagePrompt: 'Friendly dentist in white coat smiling in a modern dental office, clean professional environment, approachable expression, natural lighting, professional portrait photography',
      videoScript: 'HOOK (0–5s): "I\'m a dentist. Here\'s what I actually do for my own teeth — no sponsors."\nMAIN (5–25s): Walk through each habit with a quick explanation of why it works.\nOUTRO CTA (25–30s): "Follow for more dental tips. Drop your questions below."',
      postingTips: '1. Lead with your credential — "I\'m a dentist" immediately establishes authority.\n2. "No sponsors" builds trust and differentiates you from influencer content.\n3. Invite questions at the end — it drives comments and future content ideas.\n4. Post consistently — even 2x per week builds a loyal professional following.\n5. Respond to every comment, especially questions — it shows you\'re genuinely helpful.',
      variations: '1. "Myth busting" — debunk a common dental misconception.\n2. "Patient FAQ" — answer the questions you hear most often.\n3. "Before and after" — with patient permission, share a treatment transformation.',
    },
    Photographer: {
      hook: 'I charged $150 for photos 3 years ago. Last month I charged $4,500. Here\'s what changed.',
      caption: 'It wasn\'t my camera. It wasn\'t my editing software.\n\nIt was how I positioned myself.\n\nHere\'s the shift that changed everything for my photography business:\n\n1️⃣ I stopped competing on price and started competing on experience\n2️⃣ I niched down to one type of client instead of shooting everything\n3️⃣ I raised my prices before I felt "ready"\n4️⃣ I invested in my brand before my gear\n5️⃣ I started showing the story behind the shot, not just the shot\n\nThe work didn\'t change. The positioning did.\n\nWhat\'s holding you back from raising your rates? 👇',
      cta: 'Follow for weekly business tips for photographers — and DM me if you want to work together.',
      hashtags: '#Photographer #PhotographyBusiness #CreativeBusiness #PhotographyTips #FreelancePhotographer #PhotographyMarketing #PricingStrategy #CreativeEntrepreneur #PhotographyLife #BusinessGrowth',
      imagePrompt: 'Professional photographer with camera in a beautiful natural setting, golden hour light, artistic composition, behind-the-scenes photography feel, warm tones',
      videoScript: 'HOOK (0–5s): "$150 to $4,500 — same photographer, same camera. Here\'s what changed."\nMAIN (5–25s): Walk through the 5 positioning shifts with brief examples.\nOUTRO CTA (25–30s): "Follow for more. DM me to work together."',
      postingTips: '1. Lead with a specific, relatable number — it immediately hooks your target audience.\n2. Share the "how" not just the "what" — actionable content gets saved and shared.\n3. Ask a question at the end to invite engagement and learn what your audience needs.\n4. Post your best work consistently — your portfolio is your most powerful marketing.\n5. Engage with other photographers\' content — community builds visibility.',
      variations: '1. "Behind the shot" — show the setup and the final image side by side.\n2. "Client experience" — walk through what it\'s like to work with you.\n3. "Gear vs. skill" — debunk the myth that better gear equals better photos.',
    },
  },
  Facebook: {
    Church: {
      hook: '🙏 This Sunday\'s message is one we\'ve been preparing for months. You don\'t want to miss it.',
      caption: 'This Sunday we\'re starting a brand new series: "Finding Peace in a Noisy World."\n\nIn a time when anxiety is at an all-time high, we believe there\'s a better way to live.\n\nJoin us this Sunday at 9am or 11am — in person or online.\n\n📍 [Church Address]\n💻 Live stream link in comments\n\nBring a friend. Everyone is welcome. 🤍',
      cta: 'Join us this Sunday — in person or online. Everyone is welcome.',
      hashtags: '#Church #Sunday #Faith #Community #ChurchFamily #Worship #ChristianLife #SermonSeries #FindingPeace #Welcome',
      imagePrompt: 'Warm and welcoming church interior with soft light streaming through windows, empty pews with warm wood tones, peaceful and inviting atmosphere, professional architectural photography',
      videoScript: 'HOOK (0–5s): Pastor speaking directly to camera: "This Sunday\'s message is something I\'ve been waiting to share for months."\nMAIN (5–25s): Brief preview of the series theme, clips of community, worship, and welcoming atmosphere.\nOUTRO CTA (25–30s): Service times, address, and live stream info. "Everyone is welcome."',
      postingTips: '1. Lead with the benefit to the visitor — what will they experience or receive?\n2. Always include service times, address, and online access in every post.\n3. Post mid-week (Tuesday–Thursday) to build anticipation for Sunday.\n4. Respond to every comment — especially from first-time visitors asking questions.\n5. Use visuals that show real people and genuine community, not just buildings.',
      variations: '1. "Meet our team" — introduce a staff member or volunteer.\n2. "Community spotlight" — share a member\'s story with permission.\n3. "This week\'s message" — post a key quote or takeaway after Sunday service.',
    },
  },
  X: {
    'AI Startup': {
      hook: 'We just shipped a feature that took our team 3 months to build. Here\'s what we learned.',
      caption: 'Building in public means sharing the hard parts too.\n\nThis feature failed twice before it worked. Here\'s what we got wrong — and what finally clicked.\n\nThread 🧵👇',
      cta: 'Follow us for weekly build-in-public updates — we share everything.',
      hashtags: '#BuildInPublic #Startup #AIStartup #ProductDevelopment #Founder #TechStartup #SaaS #IndieHacker #Entrepreneurship #ShipIt',
      imagePrompt: 'Modern tech startup office with team collaborating around screens, clean minimal aesthetic, natural light, candid working atmosphere, professional startup photography',
      videoScript: 'HOOK (0–5s): "We shipped something big today. It took 3 months and failed twice. Here\'s the story."\nMAIN (5–25s): Walk through the challenge, the failed attempts, and the breakthrough moment.\nOUTRO CTA (25–30s): "Follow for weekly build-in-public updates."',
      postingTips: '1. Lead with a specific timeframe or number — it signals a real story, not fluff.\n2. Use threads for longer stories — they drive more engagement than single posts.\n3. Share failures as openly as wins — authenticity builds a loyal following.\n4. Post consistently — X rewards accounts that show up daily.\n5. Engage with replies quickly — the first hour of engagement matters most.',
      variations: '1. "Lessons learned" thread — what you\'d do differently.\n2. "Metrics update" — share a real number with context.\n3. "Feature preview" — show a screenshot or demo clip before launch.',
    },
    'Online Course': {
      hook: 'I made $47,000 from a course I built in a weekend. Here\'s the exact framework.',
      caption: 'No, it wasn\'t luck. Yes, I\'ll show you the whole thing.\n\nThe course took 14 hours to build. The marketing took 3 months to figure out.\n\nHere\'s what actually drove sales — thread 🧵',
      cta: 'Follow for the full framework — I\'m sharing everything this week.',
      hashtags: '#OnlineCourse #CourseCreator #DigitalProducts #PassiveIncome #OnlineBusiness #CourseCreation #InfoProduct #Entrepreneur #BuildInPublic #CreatorEconomy',
      imagePrompt: 'Course creator at a clean desk with laptop and notes, professional home office setup, warm lighting, focused and productive atmosphere, lifestyle photography',
      videoScript: 'HOOK (0–5s): "$47,000 from a course I built in a weekend — here\'s the framework."\nMAIN (5–25s): Break down the course topic selection, pricing, and the one marketing channel that drove 80% of sales.\nOUTRO CTA (25–30s): "Follow — I\'m sharing the full framework this week."',
      postingTips: '1. Lead with a specific, credible number — it immediately filters for your ideal audience.\n2. Promise a thread — it signals depth and drives follows.\n3. Deliver the value you promised — your reputation is your most valuable asset.\n4. Post consistently — course creator audiences reward reliable, valuable content.\n5. Engage with every reply — your community is your best marketing channel.',
      variations: '1. "How I validated the idea" — the research process before building.\n2. "What I\'d do differently" — honest reflection on mistakes made.\n3. "Student results" — with permission, share a student\'s success story.',
    },
  },
  YouTube: {
    Restaurant: {
      hook: '🎬 We let a Michelin-trained chef take over our kitchen for one night. Here\'s what happened.',
      caption: 'This is the video we almost didn\'t post.\n\nWhen Chef Marco walked into our kitchen, we had no idea what to expect. What happened over the next 4 hours changed how we think about food.\n\n🍽️ Watch the full video — link in description.\n\n📍 Come experience it yourself — reservations open now.',
      cta: 'Watch the full video and make a reservation — link in description.',
      hashtags: '#Restaurant #ChefLife #FoodVideo #BehindTheScenes #MichelinChef #FoodLovers #RestaurantLife #CulinaryArts #FoodDocumentary #DiningExperience',
      imagePrompt: 'YouTube thumbnail style: chef in action in a professional kitchen, dramatic lighting, intense focus, text overlay space at top, high contrast, cinematic food photography',
      videoScript: 'HOOK (0–10s): Chef walks into the kitchen. "I\'ve cooked in 3 Michelin-starred restaurants. Tonight, I\'m cooking here."\nMAIN (10–8min): Document the full evening — prep, service, guest reactions, chef commentary.\nOUTRO CTA (last 30s): Restaurant info, reservation link, subscribe CTA.',
      postingTips: '1. Lead with a compelling story hook — the "almost didn\'t post" angle creates curiosity.\n2. Invest in a strong thumbnail — it\'s the single biggest driver of YouTube clicks.\n3. Include your reservation link and address in the video description.\n4. Respond to comments in the first 24 hours — it signals to YouTube\'s algorithm.\n5. Post consistently — even monthly videos build a loyal local audience over time.',
      variations: '1. "A day in the life of our kitchen" — behind-the-scenes documentary style.\n2. "How we make our signature dish" — step-by-step cooking video.\n3. "Customer favorites" — interview regulars about their go-to orders.',
    },
  },
  Pinterest: {
    'Etsy Store': {
      hook: '📌 10 handmade gift ideas that ship in 3 days — perfect for last-minute shoppers.',
      caption: 'No more settling for generic gifts. 🎁\n\nThese handmade pieces are crafted with care and ship fast — so you can give something truly special even when time is short.\n\nShop the full collection — link in bio.',
      cta: 'Save this pin and shop the full collection — link in bio.',
      hashtags: '#HandmadeGifts #EtsyFinds #GiftIdeas #HandmadeWithLove #EtsySeller #UniqueGifts #ShopSmall #HandcraftedGoods #GiftGuide #SupportHandmade',
      imagePrompt: 'Beautiful flat lay of handmade artisan products arranged on a clean white surface, natural light, gift wrapping elements, warm and inviting aesthetic, Pinterest-style product photography',
      videoScript: 'HOOK (0–5s): "10 handmade gifts that ship in 3 days — save this for later."\nMAIN (5–25s): Quick showcase of each product with name and price, warm lifestyle shots.\nOUTRO CTA (25–30s): "Shop the full collection — link in bio."',
      postingTips: '1. Lead with a specific number and a clear benefit — "10 gifts that ship fast" is highly saveable.\n2. Vertical images (2:3 ratio) perform best on Pinterest — design for the format.\n3. Include your keywords in the pin title and description for Pinterest SEO.\n4. Save this pin to multiple relevant boards to maximize discovery.\n5. Post consistently — Pinterest rewards accounts that pin regularly.',
      variations: '1. "Gift guide by price" — organize by budget range.\n2. "How it\'s made" — show the crafting process in a short video pin.\n3. "Customer favorites" — curate your best-reviewed items into a collection.',
    },
  },
};

const PLATFORMS = [
  { value: 'Instagram', icon: Camera, color: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/20' },
  { value: 'Facebook', icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  { value: 'TikTok', icon: Music, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
  { value: 'YouTube', icon: Video, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  { value: 'Pinterest', icon: ImageIcon, color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20' },
  { value: 'LinkedIn', icon: Briefcase, color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-400/20' },
  { value: 'X', icon: AtSign, color: 'text-foreground', bg: 'bg-white/5', border: 'border-white/10' },
];

const CONTENT_TYPES_BY_PLATFORM: Record<string, string[]> = {
  Instagram: ['Restaurant', 'Coffee Shop', 'Tattoo Studio'],
  Facebook: ['Church'],
  TikTok: ["Children's Book", 'Etsy Store', 'Fitness Coach'],
  YouTube: ['Restaurant'],
  Pinterest: ['Etsy Store'],
  LinkedIn: ['Realtor', 'Dentist', 'Photographer'],
  X: ['AI Startup', 'Online Course'],
};

const OUTPUT_TABS = [
  { key: 'hook', label: 'Hook', icon: Zap },
  { key: 'caption', label: 'Caption', icon: FileText },
  { key: 'cta', label: 'Call-to-Action', icon: MessageSquare },
  { key: 'hashtags', label: 'Hashtags', icon: Hash },
  { key: 'imagePrompt', label: 'Image Prompt', icon: ImageIcon },
  { key: 'videoScript', label: 'Video Script', icon: Video },
  { key: 'postingTips', label: 'Posting Tips', icon: Lightbulb },
  { key: 'variations', label: 'Variations', icon: Layers },
];

// ─── Watermark overlay component ─────────────────────────────────────────────

function DemoWatermark() {
  return (
    <div
      className="absolute inset-0 pointer-events-none select-none z-10 flex items-center justify-center overflow-hidden rounded-xl"
      aria-hidden="true"
    >
      <span
        className="text-[13px] font-700 tracking-widest uppercase whitespace-nowrap"
        style={{
          transform: 'rotate(-35deg)',
          color: 'rgba(255,255,255,0.18)',
          textShadow: '0 1px 3px rgba(0,0,0,0.5)',
          letterSpacing: '0.15em',
          userSelect: 'none',
        }}
      >
        Built with AIMaker Studio
      </span>
      <span
        className="absolute bottom-2 right-2 text-[10px] font-600 tracking-wide px-2 py-1 rounded-md"
        style={{
          background: 'rgba(0,0,0,0.55)',
          color: 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255,255,255,0.15)',
        }}
      >
        Built with AIMaker Studio
      </span>
    </div>
  );
}

// ─── Unlock CTA overlay ───────────────────────────────────────────────────────

function UnlockOverlay({ onDismiss }: { onDismiss: () => void }) {
  const { t } = useLanguage();
  const demo = t?.demo as Record<string, string> | undefined;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="glass-card rounded-2xl p-8 max-w-md w-full border border-primary/30 text-center">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Lock size={24} className="text-primary" />
        </div>
        <h3 className="text-xl font-700 text-foreground mb-2">
          {demo?.unlockTitle ?? 'Unlock the Full AIMaker Studio Experience'}
        </h3>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          {demo?.unlockDesc ?? 'Create, save, and download your own content with your AIMaker Studio plan.'}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            href="/sign-up-login-screen"
            className="btn-primary px-6 py-2.5 rounded-xl text-sm font-600"
          >
            {demo?.createAccount ?? 'Create Account'}
          </Link>
          <Link
            href="/pricing"
            className="px-6 py-2.5 rounded-xl text-sm font-600 border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all"
          >
            {demo?.viewPricing ?? 'View Pricing'}
          </Link>
        </div>
        <button
          onClick={onDismiss}
          className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {demo?.continueBrowsing ?? 'Continue browsing demo'}
        </button>
      </div>
    </div>
  );
}

// ─── Upgrade card shown after limit is reached ────────────────────────────────

function DemoLimitUpgradeCard() {
  const { t } = useLanguage();
  const demo = t?.demo as Record<string, string> | undefined;
  return (
    <div className="glass-card rounded-2xl p-6 border border-primary/30 bg-gradient-to-br from-primary/8 via-purple-500/5 to-cyan-500/5 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
          <Star size={18} className="text-primary" />
        </div>
        <div>
          <h3 className="text-base font-700 text-foreground leading-snug">
            {demo?.limitTitle ?? "You've explored the AIMaker Studio Demo"}
          </h3>
        </div>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">
        {demo?.limitDesc ?? 'Create your AIMaker Studio account to continue building your ideas with DreamDNA™.'}
      </p>
      <div className="flex flex-col gap-2">
        <Link
          href="/sign-up-login-screen"
          className="btn-primary py-2.5 rounded-xl text-sm font-600 text-center"
        >
          {demo?.startDreaming ?? 'Start Dreaming'}
        </Link>
        <Link
          href="/pricing"
          className="py-2.5 rounded-xl text-sm font-600 text-center border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all"
        >
          {demo?.viewPricing ?? 'View Pricing'}
        </Link>
      </div>
    </div>
  );
}

// ─── Demo remaining counter ───────────────────────────────────────────────────

function DemoCounter({ used, max }: { used: number; max: number }) {
  const { t } = useLanguage();
  const demo = t?.demo as Record<string, string> | undefined;
  const remaining = max - used;
  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/4 border border-border">
      <span className="text-[11px] font-600 text-muted-foreground uppercase tracking-wider">
        {demo?.counterLabel ?? 'Demo Samples Remaining'}
      </span>
      <span
        className={`text-[11px] font-700 tabular-nums ${
          remaining === 0
            ? 'text-red-400'
            : remaining === 1
            ? 'text-amber-400' :'text-emerald-400'
        }`}
      >
        {remaining} / {max}
      </span>
    </div>
  );
}

// ─── Preview-only locked content panel ───────────────────────────────────────

function LockedContentPanel({
  previewText,
  onUnlock,
  previewOnlyMsg,
}: {
  previewText: string;
  onUnlock: () => void;
  previewOnlyMsg: string;
}) {
  return (
    <div className="relative" style={{ userSelect: 'none', WebkitUserSelect: 'none' }}>
      {/* Preview text — visible, no selection */}
      <pre
        className="text-sm text-foreground leading-relaxed whitespace-pre-wrap font-sans"
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
        onCopy={(e) => e.preventDefault()}
        onContextMenu={(e) => e.preventDefault()}
      >
        {previewText}
      </pre>
      {/* Locked portion — blurred placeholder lines with fade gradient */}
      <div
        className="relative mt-2 rounded-lg overflow-hidden"
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
        onCopy={(e) => e.preventDefault()}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Blurred placeholder lines — no dark background */}
        <div className="flex flex-col gap-2 py-2 px-1">
          {[85, 70, 90, 55].map((w, i) => (
            <div
              key={i}
              className="h-2.5 rounded-full bg-foreground/15"
              style={{ width: `${w}%`, filter: 'blur(4px)' }}
            />
          ))}
        </div>
        {/* Smooth fade gradient — fades into card background, no dark block */}
        <div
          className="absolute inset-0 rounded-lg pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, transparent 0%, transparent 20%, rgba(var(--card-rgb, 15,15,20), 0.75) 55%, rgba(var(--card-rgb, 15,15,20), 0.97) 100%)',
          }}
        />
        {/* Lock message — positioned at bottom of fade */}
        <div className="absolute bottom-0 inset-x-0 flex items-center justify-center pb-2">
          <button
            onClick={onUnlock}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-600 bg-primary/15 hover:bg-primary/25 text-primary border border-primary/30 transition-all backdrop-blur-sm"
          >
            <Lock size={11} />
            {previewOnlyMsg}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Demo Page ───────────────────────────────────────────────────────────

export default function DemoPage() {
  const { t } = useLanguage();
  const demo = t?.demo as Record<string, string> | undefined;

  const [selectedPlatform, setSelectedPlatform] = useState('Instagram');
  const [selectedContentType, setSelectedContentType] = useState('Restaurant');
  const [prompt, setPrompt] = useState('');
  const [activeTab, setActiveTab] = useState('hook');
  const [isSimulating, setIsSimulating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);

  // localStorage-based usage tracking — no IP, no AI calls
  const [demoUsed, setDemoUsed] = useState(0);
  const [usageLoaded, setUsageLoaded] = useState(false);

  // Restore last demo state from localStorage
  useEffect(() => {
    setDemoUsed(getDemoUsageCount());
    const lastState = loadDemoLastState();
    if (lastState) {
      const validPlatform = PLATFORMS.find((p) => p.value === lastState.platform);
      if (validPlatform) {
        setSelectedPlatform(lastState.platform);
        const types = CONTENT_TYPES_BY_PLATFORM[lastState.platform] ?? ['Restaurant'];
        if (types.includes(lastState.contentType)) {
          setSelectedContentType(lastState.contentType);
        } else {
          setSelectedContentType(types[0]);
        }
        if (lastState.hasGenerated) {
          setHasGenerated(true);
          const validTab = OUTPUT_TABS.find((tab) => tab.key === lastState.activeTab);
          setActiveTab(validTab ? lastState.activeTab : 'hook');
        }
      }
    }
    setUsageLoaded(true);
  }, []);

  // Persist state changes to localStorage
  useEffect(() => {
    if (!usageLoaded) return;
    saveDemoLastState({
      platform: selectedPlatform,
      contentType: selectedContentType,
      activeTab,
      hasGenerated,
    });
  }, [selectedPlatform, selectedContentType, activeTab, hasGenerated, usageLoaded]);

  const limitReached = usageLoaded && demoUsed >= DEMO_MAX_GENERATIONS;

  const contentTypes = CONTENT_TYPES_BY_PLATFORM[selectedPlatform] ?? ['Restaurant'];

  const handlePlatformChange = (platform: string) => {
    setSelectedPlatform(platform);
    const types = CONTENT_TYPES_BY_PLATFORM[platform] ?? ['Restaurant'];
    setSelectedContentType(types[0]);
    setHasGenerated(false);
    setActiveTab('hook');
  };

  const handleSimulateGenerate = () => {
    if (isSimulating || limitReached) return;
    setIsSimulating(true);
    setHasGenerated(false);
    setTimeout(() => {
      const newCount = Math.min(demoUsed + 1, DEMO_MAX_GENERATIONS);
      persistDemoUsage(newCount);
      setDemoUsed(newCount);
      setIsSimulating(false);
      setHasGenerated(true);
      setActiveTab('hook');
    }, 1800);
  };

  const getDemoOutput = (): Record<string, string> | null => {
    const platformOutputs = DEMO_OUTPUTS[selectedPlatform];
    if (platformOutputs) {
      const contentOutput = platformOutputs[selectedContentType];
      if (contentOutput) return contentOutput;
      const firstKey = Object.keys(platformOutputs)[0];
      return platformOutputs[firstKey] ?? null;
    }
    return DEMO_OUTPUTS['TikTok']['Fitness Coach'];
  };

  const demoOutput = getDemoOutput();
  const activeContent = demoOutput?.[activeTab] ?? '';
  const selectedPlatformMeta = PLATFORMS.find((p) => p.value === selectedPlatform);

  const previewOnlyMsg = demo?.previewOnlyMsg ?? 'Preview only — Start Dreaming to create your complete custom version.';
  const { preview: previewContent, hasMore } = activeContent
    ? getPreviewContent(activeTab, activeContent)
    : { preview: '', hasMore: false };

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      {showUnlock && <UnlockOverlay onDismiss={() => setShowUnlock(false)} />}

      <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/8 border border-primary/20 mb-5">
            <Info size={13} className="text-primary" />
            <span className="text-xs font-600 text-primary">
              {demo?.demoBadge ?? 'Interactive Demo — Preview Only'}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-800 text-foreground mb-4">
            {demo?.demoTitle ?? 'See AIMaker Studio in Action'}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {demo?.demoSubtitle ?? 'Explore what AIMaker Studio creates — no account required. Sample outputs shown below.'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)] gap-6">
          {/* Left panel — controls */}
          <div className="flex flex-col gap-5">
            <div className="glass-card rounded-2xl p-5 flex flex-col gap-5 border border-border">
              {/* Platform selector */}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-600 text-muted-foreground uppercase tracking-wider">
                  {demo?.platformLabel ?? 'Platform'}
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {PLATFORMS.map(({ value, icon: PlatIcon, color, bg, border }) => (
                    <button
                      key={value}
                      onClick={() => handlePlatformChange(value)}
                      title={value}
                      className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border transition-all duration-150 ${
                        selectedPlatform === value
                          ? `${bg} ${border} ${color}`
                          : 'bg-white/3 border-border text-muted-foreground hover:bg-white/5 hover:text-foreground'
                      }`}
                    >
                      {React.createElement(PlatIcon, { size: 18 })}
                      <span className="text-[10px] font-600 leading-none">{value}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Content type */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-600 text-muted-foreground uppercase tracking-wider">
                  {demo?.contentTypeLabel ?? 'Content Type'}
                </label>
                <div className="relative">
                  <select
                    value={selectedContentType}
                    onChange={(e) => { setSelectedContentType(e.target.value); setHasGenerated(false); }}
                    className="input-dark w-full px-3 py-2.5 rounded-xl text-sm appearance-none pr-8"
                  >
                    {contentTypes.map((ct) => (
                      <option key={ct} value={ct}>{ct}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                </div>
              </div>

              {/* Prompt input */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-600 text-muted-foreground uppercase tracking-wider">
                  {demo?.promptLabel ?? 'Your Topic (optional)'}
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={demo?.promptPlaceholder ?? 'e.g. "Summer sale — 30% off all products"'}
                  rows={3}
                  className="input-dark w-full px-3 py-2.5 rounded-xl text-sm resize-none"
                  disabled={limitReached}
                />
                <p className="text-[11px] text-muted-foreground">
                  {demo?.promptNote ?? 'Demo uses prebuilt sample outputs — no AI is called.'}
                </p>
              </div>

              {/* Demo counter — always visible once loaded */}
              {usageLoaded && (
                <DemoCounter used={demoUsed} max={DEMO_MAX_GENERATIONS} />
              )}

              {/* Generate button OR upgrade card */}
              {limitReached ? (
                <DemoLimitUpgradeCard />
              ) : (
                <button
                  onClick={handleSimulateGenerate}
                  disabled={isSimulating}
                  className="btn-primary w-full py-3 rounded-xl text-sm font-600 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {isSimulating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {demo?.simulating ?? 'Preparing sample...'}
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      {demo?.generateBtn ?? 'Generate Sample'}
                    </>
                  )}
                </button>
              )}

              {/* Blocked actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowUnlock(true)}
                  className="flex-1 py-2 px-3 rounded-xl text-xs font-500 border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all flex items-center justify-center gap-1.5"
                >
                  <Lock size={11} />
                  {demo?.saveBtn ?? 'Save'}
                </button>
                <button
                  onClick={() => setShowUnlock(true)}
                  className="flex-1 py-2 px-3 rounded-xl text-xs font-500 border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all flex items-center justify-center gap-1.5"
                >
                  <Lock size={11} />
                  {demo?.downloadBtn ?? 'Download'}
                </button>
              </div>
            </div>

            {/* CTA card — only show if limit not reached AND not yet generated */}
            {!limitReached && !hasGenerated && (
              <div className="glass-card rounded-2xl p-5 border border-primary/20 bg-primary/5">
                <p className="text-sm font-600 text-foreground mb-1">
                  {demo?.ctaCardTitle ?? 'Ready to create your own?'}
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  {demo?.ctaCardDesc ?? 'Create your AIMaker Studio account to continue building your ideas with DreamDNA™.'}
                </p>
                <div className="flex flex-col gap-2">
                  <Link href="/sign-up-login-screen" className="btn-primary py-2.5 rounded-xl text-sm font-600 text-center">
                    {demo?.createAccount ?? 'Create Account'}
                  </Link>
                  <Link href="/pricing" className="py-2.5 rounded-xl text-sm font-600 text-center border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all">
                    {demo?.viewPricing ?? 'View Pricing'}
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Right panel — output (min-w-0 lets tab row scroll inside the grid track) */}
          <div className="flex min-w-0 flex-col gap-4">
            {!hasGenerated && !isSimulating && (
              <div className="glass-card rounded-2xl p-12 flex flex-col items-center text-center gap-4 border border-dashed border-border relative overflow-hidden">
                <DemoWatermark />
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500/10 via-purple-500/10 to-cyan-500/10 border border-white/10 flex items-center justify-center text-3xl">
                  📱
                </div>
                <div>
                  <h3 className="text-base font-600 text-foreground mb-1">
                    {demo?.readyTitle ?? 'Sample output will appear here'}
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-xs">
                    {limitReached
                      ? (demo?.limitDesc ?? 'Create your AIMaker Studio account to continue building your ideas with DreamDNA™.')
                      : (demo?.readyDesc ?? 'Select a platform and content type, then click Generate Sample to see a realistic preview.')}
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-2 mt-2">
                  {OUTPUT_TABS.map(({ key, label, icon: TabIcon }) => (
                    <span key={key} className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-white/5 text-muted-foreground">
                      {React.createElement(TabIcon, { size: 10 })}
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {isSimulating && (
              <div className="glass-card rounded-2xl p-12 flex flex-col items-center text-center gap-4 relative overflow-hidden">
                <DemoWatermark />
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500/20 via-purple-500/20 to-cyan-500/20 border border-white/10 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
                </div>
                <p className="text-base font-600 text-foreground">
                  {demo?.generatingTitle ?? 'Preparing your sample...'}
                </p>
              </div>
            )}

            {hasGenerated && demoOutput && (
              <div className="flex min-w-0 flex-col gap-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    {selectedPlatformMeta && React.createElement(selectedPlatformMeta.icon, { size: 16, className: selectedPlatformMeta.color })}
                    <span className="text-sm font-600 text-foreground truncate">
                      {selectedPlatform} · {selectedContentType}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 font-600 shrink-0">
                      {demo?.sampleLabel ?? 'Sample Preview'}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowUnlock(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-500 bg-white/5 hover:bg-white/10 text-muted-foreground border border-border transition-all shrink-0"
                  >
                    <Lock size={11} />
                    {demo?.saveBtn ?? 'Save'}
                  </button>
                </div>

                {/* Tabs — horizontal scroll when the grid column is narrower than the full tab set */}
                <div
                  role="tablist"
                  aria-label="Sample output sections"
                  className="flex w-full max-w-full items-center gap-1 overflow-x-auto overscroll-x-contain scrollbar-hidden touch-pan-x pb-0.5"
                >
                  {OUTPUT_TABS.map(({ key, label, icon: TabIcon }) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === key}
                      onClick={() => setActiveTab(key)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-600 shrink-0 whitespace-nowrap transition-all duration-150 ${
                        activeTab === key
                          ? 'tab-active' : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                      }`}
                    >
                      {React.createElement(TabIcon, { size: 12 })}
                      {label}
                    </button>
                  ))}
                </div>

                {/* Content card with watermark — text selection disabled */}
                <div
                  className="glass-card rounded-2xl p-5 flex flex-col gap-4 relative overflow-hidden"
                  style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                  onCopy={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <DemoWatermark />
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-600 text-foreground">
                      {OUTPUT_TABS.find((tab) => tab.key === activeTab)?.label}
                    </span>
                    {/* Copy button locked */}
                    <button
                      onClick={() => setShowUnlock(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-500 bg-white/5 hover:bg-white/10 text-muted-foreground transition-all"
                    >
                      <Lock size={11} />
                      {demo?.copyBtn ?? 'Copy'}
                    </button>
                  </div>
                  <div className="border-t border-border pt-4">
                    {hasMore ? (
                      <LockedContentPanel
                        previewText={previewContent}
                        onUnlock={() => setShowUnlock(true)}
                        previewOnlyMsg={previewOnlyMsg}
                      />
                    ) : (
                      <pre
                        className="text-sm text-foreground leading-relaxed whitespace-pre-wrap font-sans"
                        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                        onCopy={(e) => e.preventDefault()}
                        onContextMenu={(e) => e.preventDefault()}
                      >
                        {previewContent}
                      </pre>
                    )}
                  </div>
                </div>

                {/* Unlock banner */}
                <div className="glass-card rounded-2xl p-5 border border-primary/20 bg-primary/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-600 text-foreground mb-0.5">
                      {demo?.unlockBannerTitle ?? 'Unlock the full AIMaker Studio experience'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {demo?.unlockBannerDesc ?? 'Create, save, and download your own content with your AIMaker Studio plan.'}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Link href="/sign-up-login-screen" className="btn-primary px-4 py-2 rounded-xl text-xs font-600">
                      {demo?.createAccount ?? 'Create Account'}
                    </Link>
                    <Link href="/pricing" className="px-4 py-2 rounded-xl text-xs font-600 border border-border text-muted-foreground hover:text-foreground transition-all">
                      {demo?.viewPricing ?? 'View Pricing'}
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Feature highlights */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { emoji: '🎨', title: demo?.feat1Title ?? 'Image Studio', desc: demo?.feat1Desc ?? 'Generate stunning AI images from text prompts' },
            { emoji: '🎬', title: demo?.feat2Title ?? 'Video Studio', desc: demo?.feat2Desc ?? 'Create professional AI videos in minutes' },
            { emoji: '📱', title: demo?.feat3Title ?? 'Social Media Studio', desc: demo?.feat3Desc ?? 'Complete content packages for every platform' },
          ].map((feat, i) => (
            <div key={i} className="glass-card rounded-2xl p-6 border border-border text-center">
              <div className="text-3xl mb-3">{feat.emoji}</div>
              <h3 className="text-sm font-700 text-foreground mb-1">{feat.title}</h3>
              <p className="text-xs text-muted-foreground">{feat.desc}</p>
            </div>
          ))}
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}
